<template>
  <div>
    <slot name="splitTop" />
    <div ref="startElement" />
    <component
      :is="isPkgCountMoreThanOne ? 'ul' : 'div'"
      class="ulReset"
    >
      <component
        :is="isPkgCountMoreThanOne ? 'li' : 'div'"
        v-for="pkg in packages"
        :key="pkg.discriminator.value"
        :class="$style.pkgListItem"
      >
        <lv-heading
          v-if="isPkgCountMoreThanOne"
          :tag="packageHeadingTag"
          level="4"
          version2
        >
          <span :class="$style.pkgHeading" v-text="pkg.shipsFrom" />
        </lv-heading>
        <ul :class="['ulReset', $style.pkgItems]">
          <li v-for="item in pkg.items" :key="item.uuid">
            <aw-cart-product-list-item
              :heading-tag="itemHeadingTag"
              :product-item="item"
              :checkout-type="checkoutType"
              :background="$styleVariables.colorBackground3"
              :highlighting="uncreditableItems.includes(parseInt(item.objectId))"
              :is-uncreditable="uncreditableItems.includes(parseInt(item.objectId))"
            />
          </li>
        </ul>
      </component>
    </component>
    <div ref="endElement" />
    <div :class="$style.separator" />
    <div :class="$style.summary">
      <aw-order-split-summary :checkout-type="checkoutType" :is-floating="isSummaryFloating" />
    </div>
  </div>
</template>

<script setup>
  import { inject } from 'vue';
  import { calculateHeadingChildren } from '~~/common/utils/headingTag';
  import { useOrderSplit } from '~~/common/composables/useOrderSplit';
  import { LvHeading } from '~~/common/components/loginet-vue-shop/index';
  import AwOrderSplitSummary from '~~/shop/app/components/AwOrderSplitSummary.vue';
  import AwCartProductListItem from '~~/common/components/Common/CartProductList/AwCartProductListItem.vue';

  const props = defineProps({
    checkoutType: {
      type: String,
      required: true,
      default: '',
    },
    headingTag: {
      type: String,
      required: true,
      default: 'h3',
    },
  });

  const { getSplitByCheckoutType } = useOrderSplit();

  const startElement = ref(null);
  const endElement = ref(null);

  const uncreditableItems = inject('uncreditableItems');

  const split = computed(() => getSplitByCheckoutType(props.checkoutType));

  const packages = computed(() => {
    return Array.isArray(split.value?.packages) ? split.value.packages : [];
  });

  const isPkgCountMoreThanOne = computed(() => packages.value.length > 1);

  const packageHeadingTag = computed(() => {
    return calculateHeadingChildren(props.headingTag);
  });

  const itemHeadingTag = computed(() => {
    return isPkgCountMoreThanOne.value ? calculateHeadingChildren(packageHeadingTag.value) : packageHeadingTag.value;
  });

  const isSummaryFloating = ref(false);

  watch(
    createIntersectionRef({
      startRef: startElement,
      endRef: endElement,
    }),
    ({ start, end }) => {
      const { /* isUp: isStartUp, */isDown: isStartDown } = start;
      const { /* isUp: isEndUp, */isDown: isEndDown } = end;
      isSummaryFloating.value = !isStartDown && isEndDown;
    },
  );

  function createIntersectionRef ({
    startRef,
    endRef,
  }) {

    let observer = null;
    let startEl = null;
    let endEl = null;
    let startState = {};
    let endState = {};
    const intersectionStateRef = ref({});

    onMounted(() => {
      [startEl, endEl] = [startRef.value, endRef.value];
      if (startEl && endEl) {
        observer = new IntersectionObserver(ioCb);
        for (const el of [startEl, endEl]) {
          observer.observe(el);
        }
      }
    });

    onUnmounted (() => {
      if (observer) {
        observer.disconnect();
      }
    });

    return intersectionStateRef;

    function ioCb (entries) {
      const startEntry = entries.find(e => e.target === startEl);
      const endEntry = entries.find(e => e.target === endEl);

      if (startEntry) {
        startState = getUpDownState(startEntry);
      }
      if (endEntry) {
        endState = getUpDownState(endEntry);
      }

      intersectionStateRef.value = {
        start: startState,
        end: endState,
      };
    }

    function getUpDownState (entry) {
      if (!entry) {
        return {
          isUp: false,
          isDown: false,
        };
      }
      const { rootBounds, boundingClientRect } = entry;
      return {
        isUp: rootBounds?.top > boundingClientRect?.bottom,
        isDown: rootBounds?.bottom < boundingClientRect?.top,
      };
    }
  }
</script>

<style module lang="scss" rel="stylesheet/scss">
.separator {
  margin: 0 calc(-1 * var(--awBasketTabSidePadding));
  padding-top: 12px;
  border-bottom: 1px dashed $color-dash-separator;

  @include tablet (min) {
    padding-top: 24px;
  }
}

.summary {
  padding-top: 12px;

  @include desktop-medium (min) {
    padding-top: 24px;
  }
}

.pkgItems {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.pkgHeading {
  display: block;
  padding: 12px 8px;

  @include tablet (min) {
    padding: 0 16px 12px;
  }
}

.pkgListItem {
  &:not(:first-child) {
    padding-top: 20px;

    @include tablet(min) {
      padding-top: 32px;
    }
  }
}
</style>
